import { useEffect } from "react";
import { cn } from "@/lib/utils";
import { useInView } from "@/hooks/use-in-view";
import {
  JobPostProvider,
  type JobPostProviderProps,
} from "../../matching/job-post-provider";
import {
  MatchStrengthBadge,
  type JobMatch,
} from "@/features/job-post/components/match-strength-badge";

type RecommendedJob = JobPostProviderProps["jobData"] & {
  id: string;
  match: JobMatch;
};

type RecommendedJobItemProps = {
  job: RecommendedJob;
  /** Collapses the item out of the list while its decision is saved. */
  isLeaving: boolean;
  onInterestChange: NonNullable<JobPostProviderProps["onInterestChange"]>;
  onBottomSeen: (jobId: string) => void;
};

/** How long the end of the text must stay on screen to count as read. */
const VIEWED_DWELL_MS = 1500;

/**
 * Calls `onBottomSeen` once the end of this job's text area (just above the
 * action buttons) has been continuously in view for more than
 * `VIEWED_DWELL_MS`. Leaving view before then cancels the timer, so scrolling
 * past without pausing never counts. Each item mounts its own sentinel under a
 * job-id key, so observer and timer state can never carry over between jobs.
 */
function JobPostViewedSentinel({
  jobId,
  onBottomSeen,
}: {
  jobId: string;
  onBottomSeen: (jobId: string) => void;
}) {
  const { ref, isInView } = useInView<HTMLDivElement>();

  useEffect(() => {
    if (!isInView) {
      return;
    }
    const timer = setTimeout(() => onBottomSeen(jobId), VIEWED_DWELL_MS);
    return () => clearTimeout(timer);
  }, [isInView, jobId, onBottomSeen]);

  return <div ref={ref} className="h-px" />;
}

/**
 * One list entry. The grid-rows 1fr -> 0fr transition animates the height to
 * zero (including the gap below the card), so the cards underneath glide up
 * instead of jumping when a decided job is removed. New entries fade in.
 */
export function RecommendedJobItem({
  job,
  isLeaving,
  onInterestChange,
  onBottomSeen,
}: RecommendedJobItemProps) {
  return (
    <li
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
        isLeaving ? "grid-rows-[0fr] opacity-0" : "grid-rows-[1fr] opacity-100",
      )}
      aria-hidden={isLeaving}
    >
      <div
        className={cn(
          "min-h-0",
          isLeaving && "pointer-events-none overflow-hidden",
        )}
      >
        <div className="pb-4 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-300">
          <JobPostProvider
            jobData={job}
            onInterestChange={onInterestChange}
            enableSwipe={false}
            titleAs="h2"
            headerAccessory={<MatchStrengthBadge match={job.match} />}
            textEndMarker={
              <JobPostViewedSentinel
                key={job.id}
                jobId={job.id}
                onBottomSeen={onBottomSeen}
              />
            }
          />
        </div>
      </div>
    </li>
  );
}
